/**
 * ForgotPassword page — request a password reset link.
 */

import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/Spinner';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError(null);

    try {
      await apiFetch('/v1/auth/forgot-password', {
        method: 'POST',
        body: { email: email.trim() },
      });
      setSubmitted(true);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to send reset link.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your email to receive a reset link">
      {submitted ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-slate-300">
            If an account exists for <span className="font-semibold">{email}</span>, a password
            reset link has been sent.
          </p>
          <Link to="/login" className="inline-block text-sm text-blue-400 hover:text-blue-300">
            ← Back to login
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="forgot-password-email"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-400"
            >
              Email
            </label>
            <input
              id="forgot-password-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              disabled={isSubmitting}
              required
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-800 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" /> Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>

          <p className="text-center text-xs text-slate-500">
            <Link to="/login" className="text-blue-400 hover:text-blue-300">
              ← Back to login
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
